import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Receipt, ShieldOff, AlertTriangle, TrendingUp } from 'lucide-react';
import { api } from '../../services/api';
import EmptyState from '../EmptyState';
import { HubPanel, HubSurface, HubTable } from './HubPanel';
import Kpi from '../ui/Kpi';
import Badge from '../ui/Badge';
import ForwardLink from '../details/ForwardLink';
import { InlineError, Spinner, apiErrorMessage, formatIls, isForbidden, useDateFormatter } from '../sales/shared';
import { formatNumber } from '../../utils/format';
import MissingCostWarning from '../MissingCostWarning';
import PendingDecision from '../ui/PendingDecision';
import { profitRowMarkerIds } from '../../pendingDecisions';
import ProfitPeriodPicker from '../profitability/ProfitPeriodPicker';
import ServerFigures from '../profitability/ServerFigures';
import {
  customerSummaryKeys, defaultPeriod, periodKey, periodParams, serverLine, type CustomerProfit, type ProfitPeriod,
} from '../profitability/profitabilityModel';

function NoPermission({ text }: { text: string }) {
  return (
    <HubSurface padded className="flex items-start gap-3 text-sm text-surface-700">
      <ShieldOff size={18} className="text-surface-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <p>{text}</p>
    </HubSurface>
  );
}

/**
 * The customer's billing obligations, outstanding balance and debt flag —
 * read-only here; obligations are managed on the Obligations screen. The data
 * is permission-protected server-side; a 403 renders a "no permission" state.
 */
export function BillingTab({ orgId }: { orgId: string }) {
  const { t } = useTranslation();
  const fmtDate = useDateFormatter();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['organization-billing', orgId],
    queryFn: () => api.getOrganizationBilling(orgId),
    enabled: !!orgId,
    retry: false,
  });
  const billing = data?.data;
  const rows = billing?.obligations?.rows ?? [];

  return (
    <HubPanel
      title={t('organization.tabBilling')}
      subtitle={t('organization.billing.subtitle')}
      action={
        <ForwardLink to="/obligations">{t('organization.billing.openObligations')}</ForwardLink>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : isError && isForbidden(error) ? (
        <NoPermission text={t('organization.billing.noPermission')} />
      ) : isError || !billing ? (
        <InlineError>{apiErrorMessage(error, t('organization.billing.loadError'))}</InlineError>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Kpi
              label={t('organization.billing.balance')}
              value={formatIls(billing.balance)}
              tone={billing.in_debt ? 'danger' : undefined}
              hint={billing.in_debt ? (
                <span className="inline-flex items-center gap-1 font-medium text-danger-700">
                  <AlertTriangle size={14} aria-hidden="true" /> {t('organization.billing.inDebt')}
                </span>
              ) : t('organization.billing.noDebt')}
            />
          </div>
          {rows.length === 0 ? (
            <HubSurface>
              <EmptyState icon={Receipt} title={t('organization.billing.empty')} />
            </HubSurface>
          ) : (
            <HubSurface>
              <HubTable>
                <table className="table-modern">
                  <thead>
                    <tr>
                      <th>{t('organization.billing.obligation')}</th>
                      <th className="hidden md:table-cell">{t('common.type')}</th>
                      <th className="text-end">{t('organization.billing.amount')}</th>
                      <th className="hidden lg:table-cell text-end">{t('organization.billing.issueDate')}</th>
                      <th className="hidden sm:table-cell text-end">{t('organization.billing.dueDate')}</th>
                      <th>{t('common.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((o) => (
                      <tr key={o.id}>
                        <td className="font-medium text-surface-900 max-w-xs truncate">{o.title || '—'}</td>
                        <td className="hidden md:table-cell text-surface-600">{t(`billing.type_${o.obligation_type}`, o.obligation_type)}</td>
                        <td className="text-end tabular-nums whitespace-nowrap">{formatIls(o.amount)}</td>
                        <td className="hidden lg:table-cell text-end tabular-nums whitespace-nowrap text-surface-600">{fmtDate(o.issue_date)}</td>
                        <td className="hidden sm:table-cell text-end tabular-nums whitespace-nowrap text-surface-600">{fmtDate(o.due_date)}</td>
                        <td>
                          <Badge tone={o.payment_status === 'paid' ? 'success' : o.overdue ? 'danger' : 'warning'} dot>
                            {o.payment_status === 'paid'
                              ? t('organization.billing.paid')
                              : o.overdue ? t('organization.billing.overdue') : t('organization.billing.pending')}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </HubTable>
              {billing.obligations.total > rows.length && (
                <p className="text-xs text-surface-600 px-4 py-2 border-t border-surface-200">
                  {t('organization.billing.partialList', { shown: rows.length, total: billing.obligations.total })}
                </p>
              )}
            </HubSurface>
          )}
        </div>
      )}
    </HubPanel>
  );
}

/**
 * Customer profitability (sensitive, decision 120). The tab is only offered
 * when the hub summary says the viewer may see it; if the server still
 * refuses (403) the caller hides the tab via `onForbidden`.
 */
export function ProfitabilityTab({ orgId, onForbidden }: { orgId: string; onForbidden: () => void }) {
  const { t } = useTranslation();
  // P-28b (D-169): a summary of all the customer's engagements (no table) with revenue per
  // hour, for the chosen period (this month by default); an internal customer shows cost only.
  const [period, setPeriod] = useState<ProfitPeriod>(() => defaultPeriod('month'));
  const params = periodParams(period);
  const pKey = periodKey(period);
  const summaryQuery = useQuery({
    queryKey: ['profitability', 'customer', orgId, pKey],
    queryFn: async () => (await api.getCustomerProfit(orgId, params ?? undefined)).data as CustomerProfit,
    enabled: !!orgId && !!params,
    retry: false,
  });
  const projectsQuery = useQuery({
    queryKey: ['profitability', 'project', orgId, pKey],
    queryFn: () => api.getProfitability('engagement', { org_id: orgId, ...(params ?? {}) }) /* Q-DQ-067: a row per engagement */,
    enabled: !!orgId && !!params && summaryQuery.isSuccess,
    retry: false,
  });

  const forbidden = isForbidden(summaryQuery.error) || isForbidden(projectsQuery.error);
  useEffect(() => {
    if (forbidden) onForbidden();
  }, [forbidden, onForbidden]);
  if (forbidden) return null;

  const summary = summaryQuery.data;
  const rows = projectsQuery.data?.data?.rows ?? [];
  // `margin` is a ratio (same convention as the profitability widget).
  const pct = (m: number | null) => (m == null ? '—' : `${(Number(m) * 100).toFixed(1)}%`);

  return (
    <HubPanel title={t('organization.tabProfitability')} subtitle={t('organization.profitability.subtitle')}>
      <div className="mb-4"><ProfitPeriodPicker value={period} onChange={setPeriod} allowAll={false} /></div>
      {!params ? null : summaryQuery.isLoading ? (
        <Spinner />
      ) : summaryQuery.isError || !summary ? (
        <InlineError>{apiErrorMessage(summaryQuery.error, t('organization.profitability.loadError'))}</InlineError>
      ) : (
        <div className="space-y-4">
          {/* P-13 (D-109) */}
          <MissingCostWarning count={projectsQuery.data?.data?.cost_warning?.employees_missing_cost} />
          {summary.is_internal && <p className="text-sm text-text-2" data-internal-customer="">{t('profitability.customer.internalNote')}</p>}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-customer-summary="">
            {customerSummaryKeys(summary).map((k) => (
              <Kpi
                key={k}
                label={t(`profitability.customer.${k}`)}
                value={k === 'hours' ? formatNumber(summary.hours, 1)
                  : k === 'revenue' ? formatIls(summary.revenue)
                  : k === 'cost' ? formatIls(summary.cost)
                  : k === 'profit' ? formatIls(summary.profit)
                  : (summary.revenue_per_hour == null ? '—' : formatIls(summary.revenue_per_hour))}
                tone={k === 'profit' && (summary.profit ?? 0) < 0 ? 'danger' : undefined}
              />
            ))}
          </div>
          {/* P-34 (D-184): the customer's servers (every engagement type) - price, cost to OTE, profit. */}
          {!summary.is_internal && <ServerFigures line={serverLine(summary)} />}
          {projectsQuery.isLoading ? (
            <Spinner />
          ) : rows.length === 0 ? (
            <HubSurface>
              <EmptyState icon={TrendingUp} title={t('organization.profitability.noProjects')} />
            </HubSurface>
          ) : (
            <HubSurface>
              <HubTable>
                <table className="table-modern">
                  <thead>
                    <tr>
                      <th>{t('organization.profitability.engagement')}</th>
                      <th className="hidden md:table-cell text-end">{t('organization.profitability.hours')}</th>
                      {/* D-125 PM-09 (AI revenue in a service contract) / PM-10 (AI units on a non-billable task) */}
                      {!summary.is_internal && <th className="hidden lg:table-cell text-end">{t('organization.profitability.revenue')} <PendingDecision id="PM-09" /></th>}
                      <th className="hidden lg:table-cell text-end">{t('organization.profitability.cost')} <PendingDecision id="PM-10" /> <PendingDecision id="PM-27" /></th>
                      {!summary.is_internal && <th className="text-end">{t('organization.profitability.profit')}</th>}
                      {!summary.is_internal && <th className="text-end">{t('organization.profitability.margin')}</th>}
                      {/* P-16 (D-127, §32): profit per hour and the deviation from the plan. */}
                      {!summary.is_internal && <th className="hidden md:table-cell text-end">{t('organization.profitability.perHour')}</th>}
                      <th className="hidden lg:table-cell text-end">{t('organization.profitability.planVsActual')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="font-medium text-surface-900">
                          {r.name}
                          {/* P-07 (D-74 §2) — the separate "אפיון" line: cost only, revenue = its price (before VAT). */}
                          {r.kind === 'characterization' && <span className="ms-2 text-xs font-normal text-surface-600">{t('characterization.profitLine')}</span>}
                          {r.kind === 'characterization' && <> <PendingDecision id="PM-04" /></>}
                        </td>
                        <td className="hidden md:table-cell text-end tabular-nums">{formatNumber(r.hours, 1)}</td>
                        {!summary.is_internal && (
                          <td className="hidden lg:table-cell text-end tabular-nums whitespace-nowrap">
                            {formatIls(r.revenue)}
                            {/* D-125 PM-14 (project price by release month) / PM-15 (server) / PM-16 (hour bank over the purchase) */}
                            {profitRowMarkerIds(r.revenue_kinds).map((id) => <Fragment key={id}> <PendingDecision id={id} /></Fragment>)}
                          </td>
                        )}
                        <td className="hidden lg:table-cell text-end tabular-nums whitespace-nowrap">{formatIls(r.cost)}</td>
                        {!summary.is_internal && <td className={`text-end tabular-nums whitespace-nowrap ${r.profit < 0 ? 'text-danger-700' : ''}`}>{formatIls(r.profit)}</td>}
                        {!summary.is_internal && <td className="text-end tabular-nums">{pct(r.margin)}</td>}
                        {!summary.is_internal && <td className="hidden md:table-cell text-end tabular-nums whitespace-nowrap">{r.profit_per_hour == null ? '—' : formatIls(r.profit_per_hour)}</td>}
                        <td className="hidden lg:table-cell text-end tabular-nums whitespace-nowrap">
                          {r.planned_hours == null ? '—' : (
                            <span className={r.hours > (r.planned_hours ?? 0) ? 'text-danger-700' : undefined}>
                              <bdi dir="ltr">{formatNumber(r.planned_hours, 1)} / {formatNumber(r.hours, 1)}</bdi>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </HubTable>
            </HubSurface>
          )}
        </div>
      )}
    </HubPanel>
  );
}
